import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, FormControlLabel, Switch, ToggleButtonGroup, ToggleButton, CircularProgress, Tooltip, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LanguageIcon from '@mui/icons-material/Language';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import CodeIcon from '@mui/icons-material/Code';
import ArticleIcon from '@mui/icons-material/Article';
import TableChartIcon from '@mui/icons-material/TableChart';
import axios from 'axios';
import moment from 'moment';
import LoadProgress from '../../common/LoadProgress.js';
import SystemAlert from '../../common/SystemAlert.js';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';

export default function KnowledgeList() {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(null);
  const [knowledges, setKnowledges] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [alert, setAlert] = React.useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState('');

  // Sorting state
  const [sortBy, setSortBy] = React.useState('dateUpdated');
  const [sortDirection, setSortDirection] = React.useState('desc');

  // Counts state
  const [websiteCounts, setWebsiteCounts] = React.useState({});
  const [documentCounts, setDocumentCounts] = React.useState({});

  // Knowledge search modal state
  const [openSearchDialog, setOpenSearchDialog] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [includeAllKbs, setIncludeAllKbs] = React.useState(true);
  const [selectedKbIds, setSelectedKbIds] = React.useState([]);
  const [matchMode, setMatchMode] = React.useState('contains');
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState([]);
  const [searchError, setSearchError] = React.useState('');
  const [searchPage, setSearchPage] = React.useState(1);
  const [searchPageSize] = React.useState(20);
  const [searchTotalPages, setSearchTotalPages] = React.useState(0);
  const [searchTotalResults, setSearchTotalResults] = React.useState(0);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const sortedKnowledges = React.useMemo(() => {
    const sorted = [...knowledges].sort((a, b) => {
      let aValue, bValue;
      if (sortBy === 'kbName') {
        aValue = a.kbName?.toLowerCase() || '';
        bValue = b.kbName?.toLowerCase() || '';
      } else if (sortBy === 'dateCreated') {
        aValue = a.dateCreated || '';
        bValue = b.dateCreated || '';
      } else if (sortBy === 'dateUpdated') {
        aValue = a.dateUpdated || '';
        bValue = b.dateUpdated || '';
      } else {
        aValue = '';
        bValue = '';
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [knowledges, sortBy, sortDirection]);

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.post('/kbs/deleteKb', null, { params: { kbId: deleteId } });
      setKnowledges(knowledges.filter(kb => kb.id !== deleteId));
      setOpenDialog(false);
      setAlert({ severity: 'success', message: 'Knowledge base deleted successfully' });
    } catch (err) {
      console.error('Error deleting knowledge base:', err);
      setAlert({ severity: 'error', message: 'Failed to delete knowledge base' });
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDeleteConfirmText('');
  };

  const handleOpenSearchDialog = () => {
    setOpenSearchDialog(true);
    setSearchError('');
  };

  const handleCloseSearchDialog = () => {
    setOpenSearchDialog(false);
    setSearchError('');
  };

  const getSourceTypeLabel = (source) => {
    if (source === 'uploaded_file') return 'Uploaded File';
    if (source === 'google_drive') return 'Google Drive';
    if (source === 'web_scraped') return 'Web Scraped';
    return source || 'Unknown';
  };

  const getSourceTypeColor = (source) => {
    if (source === 'uploaded_file') return 'success';
    if (source === 'google_drive') return 'info';
    if (source === 'web_scraped') return 'primary';
    return 'default';
  };

  const getDocumentTypeInfo = (result) => {
    if (result?.documentSource === 'web_scraped') {
      return { kind: 'web', label: 'Web Scraped' };
    }

    const type = (result?.fileType || '').toLowerCase();
    const extension = (result?.fileName || '').split('.').pop().toLowerCase();

    if (type === 'pdf' || extension === 'pdf') return { kind: 'pdf', label: 'PDF' };
    if (type === 'word' || ['doc', 'docx', 'txt'].includes(extension)) return { kind: 'word', label: 'Word' };
    if (type === 'excel' || ['xls', 'xlsx'].includes(extension)) return { kind: 'excel', label: 'Excel' };
    if (type === 'csv' || extension === 'csv') return { kind: 'csv', label: 'CSV' };
    if (type === 'markdown' || ['md', 'markdown'].includes(extension)) return { kind: 'markdown', label: 'Markdown' };
    if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) return { kind: 'image', label: 'Image' };
    if (['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'json'].includes(extension)) return { kind: 'code', label: 'Code' };
    return { kind: 'file', label: 'File' };
  };

  const renderDocumentTypeIcon = (result) => {
    const info = getDocumentTypeInfo(result);
    const iconSx = { fontSize: '1rem' };

    if (info.kind === 'web') return <LanguageIcon sx={{ ...iconSx, color: '#2196f3' }} />;
    if (info.kind === 'pdf') return <PictureAsPdfIcon sx={{ ...iconSx, color: '#f44336' }} />;
    if (info.kind === 'word') return <ArticleIcon sx={{ ...iconSx, color: '#2196f3' }} />;
    if (info.kind === 'excel' || info.kind === 'csv') return <TableChartIcon sx={{ ...iconSx, color: '#4CAF50' }} />;
    if (info.kind === 'image') return <ImageIcon sx={{ ...iconSx, color: '#4caf50' }} />;
    if (info.kind === 'code') return <CodeIcon sx={{ ...iconSx, color: '#ff9800' }} />;
    if (info.kind === 'markdown') {
      return (
        <Box
          component='span'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 24,
            height: 20,
            px: 0.75,
            borderRadius: 1,
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            fontSize: '0.7rem',
            fontWeight: 700,
            lineHeight: 1
          }}
        >
          MD
        </Box>
      );
    }
    return <InsertDriveFileIcon sx={{ ...iconSx, color: '#9e9e9e' }} />;
  };

  const executeKnowledgeSearch = async (page = 1) => {
    const normalizedQuery = searchQuery.trim();
    if (normalizedQuery.length < 2) {
      setSearchError('Please enter at least 2 characters to search.');
      return;
    }

    if (!includeAllKbs && selectedKbIds.length === 0) {
      setSearchError('Please select at least one knowledge base or enable "Search all knowledge bases".');
      return;
    }

    try {
      setSearchLoading(true);
      setSearchError('');

      const response = await axios.post('/kbs/searchKnowledgeContent', {
        query: normalizedQuery,
        kbIds: selectedKbIds,
        includeAllKbs,
        matchMode,
        page,
        pageSize: searchPageSize
      });

      setSearchResults(response.data?.results || []);
      setSearchPage(response.data?.currentPage || page);
      setSearchTotalPages(response.data?.totalPages || 0);
      setSearchTotalResults(response.data?.totalResults || 0);
    } catch (err) {
      console.error('Error searching knowledge content:', err);
      const message = err?.response?.data?.message || 'Failed to search knowledge content. Please try again.';
      setSearchError(message);
      setSearchResults([]);
      setSearchTotalPages(0);
      setSearchTotalResults(0);
    } finally {
      setSearchLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    if (!text) {
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setAlert({ severity: 'success', message: 'URL copied to clipboard' });
    } catch {
      const tempInput = document.createElement('textarea');
      tempInput.value = text;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
      setAlert({ severity: 'success', message: 'URL copied to clipboard' });
    }
  };

  const openUrlInNewTab = (url) => {
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  React.useEffect(() => {
    const fetchKnowledges = async () => {
      try {
        setLoading(true);
        const response = await axios.post('/kbs/getAllKbs');
        setKnowledges(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching knowledge bases:', err);
        setError('Failed to load knowledge bases. Please try again later.');
        setKnowledges([]);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledges();
  }, []);

  // Fetch website and document counts when knowledge bases are fetched
  React.useEffect(() => {
    async function fetchCounts() {
      const websiteCountsTemp = {};
      const documentCountsTemp = {};

      for (const kb of knowledges) {
        // Fetch websites
        const websitesRes = await axios.post('/kbs/getKbWebsiteDetails', null, { params: { kbId: kb.id } });
        websiteCountsTemp[kb.id] = websitesRes.data ? websitesRes.data.length : 0;

        // Fetch documents
        const docsRes = await axios.post('/kbs/getKbDocuments', null, { params: { kbId: kb.id } });
        documentCountsTemp[kb.id] = docsRes.data ? docsRes.data.length : 0;
      }

      setWebsiteCounts(websiteCountsTemp);
      setDocumentCounts(documentCountsTemp);
    }

    if (knowledges.length > 0) {
      fetchCounts();
    }
  }, [knowledges]);

  React.useEffect(() => {
    if (includeAllKbs) {
      setSelectedKbIds(knowledges.map(kb => kb.id));
    }
  }, [includeAllKbs, knowledges]);

  const knowledgeToDelete = knowledges.find(kb => kb.id === deleteId);

  return (
    <div>
      {
        alert && (
          <SystemAlert open={alert !== null} severity={alert.severity} message={alert.message} />
        )
      }

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px' }}>
        <Typography variant="h5" component="h1">
          Knowledge Bases
        </Typography>

        <div style={{ float: "right", margin: '10px' }}>
          <Button variant='outlined' onClick={handleOpenSearchDialog} sx={{ mr: 1 }}>
            Search Knowledge
          </Button>
          <Button variant='outlined' onClick={() => navigate('/KnowledgeInfo')}>Add new Knowledge Source</Button>
        </div>

      </div>


      {loading ? (
        <LoadProgress open={loading}></LoadProgress>
      ) : error ? (
        <div style={{ color: 'error.main', textAlign: 'center', padding: '20px' }}>
          {error}
        </div>
      ) : (
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'kbName'}
                  direction={sortBy === 'kbName' ? sortDirection : 'asc'}
                  onClick={() => handleSort('kbName')}
                >
                  Knowledge Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'dateCreated'}
                  direction={sortBy === 'dateCreated' ? sortDirection : 'asc'}
                  onClick={() => handleSort('dateCreated')}
                >
                  Created On
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'dateUpdated'}
                  direction={sortBy === 'dateUpdated' ? sortDirection : 'asc'}
                  onClick={() => handleSort('dateUpdated')}
                >
                  Last Updated
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedKnowledges.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No knowledge bases found</TableCell>
              </TableRow>
            ) : (
              sortedKnowledges.map((kb) => (
                <TableRow
                  key={kb.id}
                  sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  <TableCell component="th" scope="row">
                    {kb.kbName}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <span style={{ display: 'flex', gap: 8 }}>
                      {websiteCounts[kb.id] > 0 && (
                        <Chip
                          label={`Websites (${websiteCounts[kb.id]})`}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      {documentCounts[kb.id] > 0 && (
                        <Chip
                          label={`Documents (${documentCounts[kb.id]})`}
                          color="success"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      {/* No chips if both counts are zero */}
                    </span>
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {kb.createdBy}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {moment(kb.dateCreated).format('MMMM Do, YYYY')}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {moment(kb.dateUpdated).format('MMMM Do, YYYY')}
                  </TableCell>
                  <TableCell align="right">
                    <EditIcon
                      sx={{ cursor: 'pointer', color: 'success.main' }}
                      onClick={() => navigate(`/KnowledgeInfo/${kb.id}`)}
                    />
                    &nbsp;
                    <DeleteIcon
                      sx={{ cursor: 'pointer', color: 'error.main' }}
                      onClick={() => handleDeleteClick(kb.id)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { width: 700, maxWidth: '100%' }
        }}
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this knowledge source:
            <Box component="div" mt={1} mb={2} sx={{ pl: 2, fontWeight: 'bold' }}>
              {knowledgeToDelete ? knowledgeToDelete.kbName : ''}
            </Box>
            <Box component="div" mb={2}>
              This action cannot be undone.
            </Box>
            <Box component="div" mb={1}>
              Type <span style={{ color: 'red', fontWeight: 'bold' }}>Delete</span> (case sensitive) to confirm.
            </Box>
          </DialogContentText>
          <Box mt={2}>
            <TextField
              autoFocus
              size="small"
              label='Type "Delete" to confirm'
              fullWidth
              variant="outlined"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            autoFocus
            variant='contained'
            disabled={deleteConfirmText !== 'Delete'}
          >
            Delete
          </Button>
          <Button onClick={handleCloseDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Knowledge Search Dialog */}
      <Dialog
        open={openSearchDialog}
        onClose={handleCloseSearchDialog}
        aria-labelledby="search-knowledge-dialog-title"
        scroll='paper'
        PaperProps={{
          sx: { width: 1000, maxWidth: '100%', maxHeight: '90vh' }
        }}
      >
        <DialogTitle
          id="search-knowledge-dialog-title"
          sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
        >
          Search Knowledge Content
        </DialogTitle>
        <DialogContent sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label='Search terms or phrase'
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Example: refund policy, onboarding checklist, API key rotation'
            />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeAllKbs}
                    onChange={(e) => setIncludeAllKbs(e.target.checked)}
                  />
                }
                label='Search all knowledge bases'
              />

              <ToggleButtonGroup
                value={matchMode}
                exclusive
                onChange={(_, value) => {
                  if (value) setMatchMode(value);
                }}
                size='small'
              >
                <ToggleButton value='contains'>Contains</ToggleButton>
                <ToggleButton value='exact_phrase'>Exact Phrase</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <FormControl fullWidth size='small' disabled={includeAllKbs}>
              <InputLabel id='kb-multiselect-label'>Knowledge Bases</InputLabel>
              <Select
                labelId='kb-multiselect-label'
                multiple
                value={selectedKbIds}
                label='Knowledge Bases'
                onChange={(event) => {
                  const value = event.target.value;
                  const normalized = (typeof value === 'string' ? value.split(',') : value)
                    .map((id) => Number(id))
                    .filter((id) => !Number.isNaN(id));
                  setSelectedKbIds(normalized);
                }}
                renderValue={(selected) => {
                  const selectedSet = new Set(selected);
                  const names = knowledges
                    .filter((kb) => selectedSet.has(kb.id))
                    .map((kb) => kb.kbName);
                  return names.join(', ');
                }}
              >
                {knowledges.map((kb) => (
                  <MenuItem key={kb.id} value={kb.id}>
                    <Checkbox checked={selectedKbIds.indexOf(kb.id) > -1} />
                    <ListItemText primary={kb.kbName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {searchError && (
              <Typography color='error' variant='body2'>
                {searchError}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                {searchTotalResults > 0
                  ? `${searchTotalResults} result${searchTotalResults === 1 ? '' : 's'} found`
                  : 'Run a search to see matching knowledge content'}
              </Typography>
              {searchLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant='body2'>Searching...</Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ maxHeight: '45vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {!searchLoading && searchResults.length === 0 ? (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 3, textAlign: 'center' }}>
                  <Typography variant='body2' color='text.secondary'>
                    No matching knowledge found.
                  </Typography>
                </Box>
              ) : (
                searchResults.map((result) => (
                  <Box
                    key={`${result.kbId}-${result.id}-${result.documentSource}`}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.5,
                      backgroundColor: 'background.paper'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 2,
                        alignItems: 'start'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, minWidth: 0 }}>
                        <Typography
                          variant='body2'
                          sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {result.kbName}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size='small'
                            label={getSourceTypeLabel(result.documentSource)}
                            color={getSourceTypeColor(result.documentSource)}
                            variant='filled'
                          />
                          <Tooltip title={getDocumentTypeInfo(result).label} placement='top'>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 24 }}>
                              {renderDocumentTypeIcon(result)}
                            </Box>
                          </Tooltip>
                        </Box>

                        <Tooltip title={result.title || result.fileName || 'Untitled'} placement='top'>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {result.title || result.fileName || 'Untitled'}
                          </Typography>
                        </Tooltip>

                        {result.documentSource === 'web_scraped' && result.siteUrl ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0, flexWrap: 'nowrap' }}>
                            <Tooltip title={result.siteUrl} placement='top'>
                              <Typography
                                variant='body2'
                                sx={{
                                  maxWidth: 'calc(100% - 44px)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  minWidth: 0,
                                  flex: '0 1 auto'
                                }}
                              >
                                {result.siteUrl}
                              </Typography>
                            </Tooltip>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15, flex: '0 0 auto' }}>
                              <Tooltip title='Copy URL'>
                                <IconButton
                                  size='small'
                                  onClick={() => copyToClipboard(result.siteUrl)}
                                  aria-label='Copy URL'
                                  sx={{ p: 0.25 }}
                                >
                                  <ContentCopyIcon fontSize='inherit' />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title='Open URL in new tab'>
                                <IconButton
                                  size='small'
                                  onClick={() => openUrlInNewTab(result.siteUrl)}
                                  aria-label='Open URL in new tab'
                                  sx={{ p: 0.25 }}
                                >
                                  <OpenInNewIcon fontSize='inherit' />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant='body2' color='text.secondary'>
                            -
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant='caption' color='text.secondary'>
                          Snippet
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 10,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {result.snippet || 'No snippet available'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </Box>

            {searchTotalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  size='small'
                  disabled={searchLoading || searchPage <= 1}
                  onClick={() => executeKnowledgeSearch(searchPage - 1)}
                >
                  Previous
                </Button>
                <Typography variant='body2' sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                  Page {searchPage} of {searchTotalPages}
                </Typography>
                <Button
                  size='small'
                  disabled={searchLoading || searchPage >= searchTotalPages}
                  onClick={() => executeKnowledgeSearch(searchPage + 1)}
                >
                  Next
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ position: 'sticky', bottom: 0, zIndex: 2, backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant='outlined'
            onClick={() => {
              setSearchQuery('');
              setSearchResults([]);
              setSearchError('');
              setSearchPage(1);
              setSearchTotalPages(0);
              setSearchTotalResults(0);
              setMatchMode('contains');
              setIncludeAllKbs(true);
            }}
          >
            Reset
          </Button>
          <Button
            variant='contained'
            onClick={() => executeKnowledgeSearch(1)}
            disabled={searchLoading}
          >
            Search
          </Button>
          <Button onClick={handleCloseSearchDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}